import { useRouterState } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { CustomLoginForm, type CustomLoginFormMessages } from "./CustomLoginForm";
import type { LoginLookupResult } from "./loginLookup";
import { useAuth, type User } from "./AuthProvider";

export type AuthenticatedAppGateProps = {
  children: ReactNode;
  loadingFallback: ReactNode;
  lookupLoginMethod: (email: string) => Promise<LoginLookupResult>;
  loginMessages?: CustomLoginFormMessages;
  supportHref?: string;
  renderAuthenticated?: (params: { children: ReactNode; user: User | null }) => ReactNode;
};

export function AuthenticatedAppGate({
  children,
  loadingFallback,
  lookupLoginMethod,
  loginMessages,
  supportHref,
  renderAuthenticated,
}: AuthenticatedAppGateProps) {
  const { isAuthenticated, isLoading, refreshAuth, user, validationError } = useAuth();
  const href = useRouterState({
    select: (state) => state.location.pathname + state.location.searchStr,
  });

  if (href.startsWith("/login/reset")) {
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{loadingFallback}</>;
  }

  if (!isAuthenticated) {
    return (
      <CustomLoginForm
        authenticate={refreshAuth}
        forgotPasswordHref={(email) =>
          `/login/reset?email=${encodeURIComponent(email)}`
        }
        initialStatusMessage={validationError ?? undefined}
        isAuthenticated={isAuthenticated}
        lookupLoginMethod={lookupLoginMethod}
        messages={loginMessages}
        onNavigate={(to) => {
          window.location.replace(to);
        }}
        redirectTo={href}
        supportHref={supportHref}
      />
    );
  }

  return <>{renderAuthenticated?.({ children, user }) ?? children}</>;
}
